/**
 * UTILIDADES DOM
 * Funciones para manipulación del DOM y gestión de eventos con TypeScript
 */

import type { CSSStyles, ViewportInfo, DOMSelectors, DOMElements } from '@types';

/**
 * Obtiene referencias a elementos DOM de forma segura
 */
export function safeQuerySelector<T extends HTMLElement = HTMLElement>(
  selector: string, 
  parent: Document | HTMLElement = document
): T | null {
  try {
    return parent.querySelector<T>(selector);
  } catch (error) {
    console.warn(`Error al buscar selector "${selector}":`, error);
    return null;
  }
}

/**
 * Obtiene múltiples referencias DOM
 */
export function getMultipleElements(
  selectors: DOMSelectors, 
  parent: Document | HTMLElement = document
): DOMElements {
  const elements: DOMElements = {};
  
  for (const [name, selector] of Object.entries(selectors)) {
    elements[name] = safeQuerySelector(selector as string, parent);
    
    if (!elements[name]) {
      console.warn(`Elemento no encontrado: ${name} (${selector})`);
    }
  }
  
  return elements;
}

/**
 * Crea elemento SVG con namespace correcto
 */
export function createSVGElement<K extends keyof SVGElementTagNameMap>(
  tagName: K, 
  attributes: Record<string, string | number> = {}
): SVGElementTagNameMap[K] {
  const element = document.createElementNS('http://www.w3.org/2000/svg', tagName);
  
  for (const [key, value] of Object.entries(attributes)) {
    element.setAttribute(key, String(value));
  }
  
  return element;
}

/**
 * Remueve elementos DOM de forma segura
 */
export function removeElements(elements: (HTMLElement | SVGElement | null)[]): void {
  elements.forEach(element => {
    if (element && element.parentNode) {
      element.parentNode.removeChild(element);
    }
  });
}

/**
 * Añade event listener con cleanup automático
 */
export function addEventListenerWithCleanup<K extends keyof HTMLElementEventMap>(
  element: HTMLElement | null, 
  event: K, 
  handler: (this: HTMLElement, ev: HTMLElementEventMap[K]) => any, 
  options: AddEventListenerOptions = {}
): () => void {
  if (!element || typeof handler !== 'function') {
    console.warn('addEventListenerWithCleanup: elemento o handler inválido');
    return () => {};
  }
  
  element.addEventListener(event, handler, options);
  
  // Retornar función de cleanup
  return () => {
    element.removeEventListener(event, handler, options);
  };
}

/**
 * Debounce para funciones que se ejecutan frecuentemente
 */
export function debounce<T extends (...args: any[]) => any>(
  func: T, 
  wait: number
): (...args: Parameters<T>) => void {
  let timeout: NodeJS.Timeout | undefined;
  
  return function executedFunction(...args: Parameters<T>) {
    const later = () => {
      clearTimeout(timeout);
      func(...args);
    };
    
    clearTimeout(timeout);
    timeout = setTimeout(later, wait);
  };
}

/**
 * Muestra/oculta elementos con transición suave
 */
export function toggleElementVisibility(
  element: HTMLElement | null, 
  show: boolean, 
  duration: number = 300
): void {
  if (!element) return;
  
  if (show) {
    element.style.display = 'block';
    element.style.opacity = '0';
    
    // Forzar reflow
    element.offsetHeight;
    
    element.style.transition = `opacity ${duration}ms ease`;
    element.style.opacity = '1';
  } else {
    element.style.transition = `opacity ${duration}ms ease`;
    element.style.opacity = '0';
    
    setTimeout(() => {
      element.style.display = 'none';
    }, duration);
  }
}

/**
 * Detecta si es dispositivo móvil
 */
export function isMobileDevice(): boolean {
  return /Android|webOS|iPhone|iPad|iPod|BlackBerry|IEMobile|Opera Mini/i.test(navigator.userAgent);
}

/**
 * Obtiene información del viewport
 */
export function getViewportInfo(): ViewportInfo {
  return {
    width: window.innerWidth,
    height: window.innerHeight,
    devicePixelRatio: window.devicePixelRatio || 1,
    ratio: window.innerWidth / window.innerHeight
  };
}

/**
 * Obtiene dimensiones de un elemento
 */
export function getElementDimensions(element: HTMLElement | null): {
  width: number;
  height: number;
  rect: DOMRect | null;
} {
  if (!element) {
    return { width: 0, height: 0, rect: null };
  }
  
  const rect = element.getBoundingClientRect();
  
  return {
    width: rect.width,
    height: rect.height,
    rect: rect
  };
}

/**
 * Aplica estilos CSS a un elemento
 */
export function applyStyles(element: HTMLElement | null, styles: CSSStyles): void {
  if (!element || !styles) return;
  
  for (const [property, value] of Object.entries(styles)) {
    (element.style as any)[property] = value;
  }
}

/**
 * Ejecuta función cuando el DOM esté listo
 */
export function onDOMReady(callback: () => void): void {
  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', callback);
  } else {
    callback();
  }
}

/**
 * Ejecuta función con delay
 */
export function executeWithDelay(func: () => void, delay: number): NodeJS.Timeout {
  return setTimeout(func, delay);
}

/**
 * Crea elemento HTML con atributos
 */
export function createElement<K extends keyof HTMLElementTagNameMap>(
  tagName: K,
  attributes: Record<string, string | number> = {},
  textContent?: string
): HTMLElementTagNameMap[K] {
  const element = document.createElement(tagName);
  
  for (const [key, value] of Object.entries(attributes)) {
    element.setAttribute(key, String(value));
  }
  
  if (textContent) {
    element.textContent = textContent;
  }
  
  return element;
}

/**
 * Busca el elemento padre más cercano que coincida con el selector
 */
export function findClosestParent(
  element: HTMLElement | null, 
  selector: string
): HTMLElement | null {
  if (!element) return null;
  
  return element.closest(selector) as HTMLElement | null;
}

/**
 * Obtiene la posición de un elemento relativa al viewport
 */
export function getElementPosition(element: HTMLElement | null): {
  x: number;
  y: number;
  width: number;
  height: number;
} {
  if (!element) {
    return { x: 0, y: 0, width: 0, height: 0 };
  }
  
  const rect = element.getBoundingClientRect();
  
  return {
    x: rect.left,
    y: rect.top,
    width: rect.width,
    height: rect.height
  };
}

/**
 * Añade clase CSS con verificación
 */
export function addClass(element: HTMLElement | null, className: string): void {
  if (element && className) {
    element.classList.add(className);
  }
}

/**
 * Remueve clase CSS con verificación
 */
export function removeClass(element: HTMLElement | null, className: string): void {
  if (element && className) {
    element.classList.remove(className);
  }
}

/**
 * Alterna clase CSS
 */
export function toggleClass(element: HTMLElement | null, className: string): boolean {
  if (!element || !className) return false;
  
  return element.classList.toggle(className);
}

/**
 * Verifica si un elemento tiene una clase
 */
export function hasClass(element: HTMLElement | null, className: string): boolean {
  if (!element || !className) return false;
  
  return element.classList.contains(className);
}

/**
 * Obtiene el valor de un atributo data
 */
export function getDataAttribute(element: HTMLElement | null, key: string): string | null {
  if (!element) return null;
  
  return element.getAttribute(`data-${key}`);
}

/**
 * Establece el valor de un atributo data
 */
export function setDataAttribute(
  element: HTMLElement | null, 
  key: string, 
  value: string | number
): void {
  if (element) {
    element.setAttribute(`data-${key}`, String(value));
  }
}

/**
 * Verifica si un elemento está visible en el viewport
 */
export function isElementInViewport(element: HTMLElement | null): boolean {
  if (!element) return false;
  
  const rect = element.getBoundingClientRect();
  const viewport = getViewportInfo();
  
  return (
    rect.top >= 0 &&
    rect.left >= 0 &&
    rect.bottom <= viewport.height &&
    rect.right <= viewport.width
  );
}